import { Component, OnInit, ViewChild } from '@angular/core';
import { ChartOptions, ChartData, ChartType, ChartEvent } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { DfsService } from '../../../services/dfs.service';
import { WeeklyForecast } from '../../../models/dfs-events.models';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-weekly-forecast',
  templateUrl: './weekly-forecast.component.html',
  styleUrls: ['./weekly-forecast.component.scss'],
  imports: [BaseChartDirective, CommonModule]
})
export class WeeklyForecastComponent implements OnInit {
  @ViewChild(BaseChartDirective) chart?: BaseChartDirective;

  isLoading = true;
  hasError = false;
  isEmpty = false;

  // Color palette for different datasets
  private lineColors: string[] = [
    'rgb(185, 28, 28)',
    'rgb(84, 112, 198)',
    'rgb(126, 34, 206)',
    'rgb(21, 128, 61)',
    'rgb(236, 72, 153)',
    'rgb(145, 204, 117)',
    'rgb(250, 200, 88)'
  ];

  // Chart configuration
  public lineChartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true, // Show legend
        position: 'top'
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const yVal = context.parsed.y ?? 0;
            return 'Forecast: ' + 'kWH' + this.numberWithCommas(yVal);
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          color: '#D8DAE5',
          tickBorderDash: [10],
        },
        ticks: {
          display: true, // Show X-axis ticks
        },
      },
      y: {
        grid: {
          color: '#D8DAE5',
          tickBorderDash: [10],
        },
        ticks: {
          // beginAtZero: true,
          callback: function(value) {
            return value + 'kWH';
          }
        }
      }
    }
  };

  public lineChartType = 'line' as const;

  public lineChartData: ChartData<'line'> = {
    labels: [],
    datasets: []
  };

  constructor(private weeklyForecastService: DfsService) {}

  ngOnInit(): void {
    const email = localStorage.getItem('_e') || '';
    const mpan = localStorage.getItem('_sf') || '';

    this.weeklyForecastService.getWeeklyForecast(mpan).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.processData(response.data);
      },
      error: (err) => {
        this.isLoading = false;
        this.hasError = true;
        console.error('Error fetching weekly forecast:', err);
      }
    });
  }

  private processData(data: WeeklyForecast[]): void {
    if (!data || data.length === 0) {
      this.isEmpty = true;
      return;
    }

    // Extract unique periods for labels
    const periods = Array.from(new Set(data.map(item => item.Period)));

    // Initialize datasets
    const datasetsMap: { [key: string]: { label: string; data: number[]; borderColor: string; backgroundColor: string; borderWidth: number; pointRadius: number } } = {};
    let colorIndex = 0;

    data.forEach(item => {
      const { Period, EventDate, Forecast } = item;

      if (!datasetsMap[EventDate]) {
        datasetsMap[EventDate] = {
          label: EventDate,
          data: [],
          borderColor: this.lineColors[colorIndex % this.lineColors.length],
          backgroundColor: '#fff',
          borderWidth: 1.8,
          pointRadius: 2
        };
        colorIndex++;
      }

      datasetsMap[EventDate].data.push(Forecast);
    });

    // Convert datasetsMap to an array
    const datasets = Object.values(datasetsMap);

    // Update chart data
    this.lineChartData.labels = periods;
    this.lineChartData.datasets = datasets;

    // Optionally, create gradient backgrounds for each dataset
    // Note: ng2-charts currently does not support gradient backgrounds out-of-the-box
    // To implement gradients, you may need to use Chart.js plugins or custom scripting
    // For simplicity, we're using solid colors here

    // Refresh the chart
    this.chart?.update();
  }

  /**
   * Utility function to format numbers with commas
   */
  private numberWithCommas(value: number): string {
    return value.toLocaleString();
  }
}
